import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

async function getAuthenticatedUser() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { getRequestHeader } = await import("@tanstack/react-start/server");
  const { createClient } = await import("@supabase/supabase-js");

  let token: string | undefined;
  const authHeader = getRequestHeader("authorization");
  if (authHeader) token = authHeader.replace("Bearer ", "");

  if (!token) {
    const cookieHeader = getRequestHeader("cookie") || "";
    const cookies = Object.fromEntries(
      cookieHeader.split(";").map((c) => {
        const [key, ...val] = c.trim().split("=");
        return [key, val.join("=")];
      })
    );
    const projectRef = (process.env.SUPABASE_URL || "").match(/\/\/([^.]+)/)?.[1] || "";
    const raw = cookies[`sb-${projectRef}-auth-token`];
    if (raw) {
      try {
        const parsed = JSON.parse(decodeURIComponent(raw));
        token = parsed.access_token || parsed[0];
      } catch {
        token = raw;
      }
    }
  }

  if (!token) throw new Error("No autorizado. Por favor iniciá sesión.");

  const userClient = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
    global: { headers: { Authorization: `Bearer ${token}` } },
    auth: { persistSession: false },
  });

  const { data: userData } = await userClient.auth.getUser();
  if (!userData?.user) throw new Error("No autorizado. Por favor iniciá sesión.");

  return { userId: userData.user.id, supabaseAdmin };
}

async function ensureAdmin(userId: string, supabaseAdmin: any) {
  const { data } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (!data) throw new Error("Acceso restringido. Solo administradores.");
}

// ---------- Wallet ----------

export const getMyWallet = createServerFn({ method: "POST" })
  .handler(async () => {
    const { userId, supabaseAdmin } = await getAuthenticatedUser();
    const { data: wallet, error } = await supabaseAdmin.rpc("get_or_create_wallet", { _user_id: userId });
    if (error) throw new Error(error.message);
    const w = Array.isArray(wallet) ? wallet[0] : wallet;
    return { wallet: w };
  });

export const getMyWalletTransactions = createServerFn({ method: "POST" })
  .handler(async () => {
    const { userId, supabaseAdmin } = await getAuthenticatedUser();
    const { data } = await supabaseAdmin
      .from("wallet_transactions")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(50);
    return { transactions: data || [] };
  });

// ---------- Payout profile ----------

const payoutProfileSchema = z.object({
  full_name: z.string().min(2).max(200),
  method: z.enum(["bank_transfer", "sinpe", "paypal", "other"]),
  document_id: z.string().max(100).optional().nullable(),
  bank_name: z.string().max(200).optional().nullable(),
  account_number: z.string().max(100).optional().nullable(),
  iban: z.string().max(100).optional().nullable(),
  sinpe_number: z.string().max(50).optional().nullable(),
  email_for_payment: z.string().email().optional().nullable().or(z.literal("")),
  notes: z.string().max(500).optional().nullable(),
});

export const getMyPayoutProfile = createServerFn({ method: "POST" })
  .handler(async () => {
    const { userId, supabaseAdmin } = await getAuthenticatedUser();
    const { data } = await supabaseAdmin
      .from("user_payout_profiles")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();
    return { profile: data };
  });

export const upsertMyPayoutProfile = createServerFn({ method: "POST" })
  .inputValidator(payoutProfileSchema)
  .handler(async ({ data }) => {
    const { userId, supabaseAdmin } = await getAuthenticatedUser();
    const payload = {
      user_id: userId,
      full_name: data.full_name,
      method: data.method,
      document_id: data.document_id || null,
      bank_name: data.bank_name || null,
      account_number: data.account_number || null,
      iban: data.iban || null,
      sinpe_number: data.sinpe_number || null,
      email_for_payment: data.email_for_payment || null,
      notes: data.notes || null,
    };
    const { data: existing } = await supabaseAdmin
      .from("user_payout_profiles")
      .select("id")
      .eq("user_id", userId)
      .maybeSingle();
    if (existing) {
      const { error } = await supabaseAdmin
        .from("user_payout_profiles")
        .update(payload)
        .eq("id", existing.id);
      if (error) throw new Error(error.message);
    } else {
      const { error } = await supabaseAdmin.from("user_payout_profiles").insert(payload);
      if (error) throw new Error(error.message);
    }
    return { success: true };
  });

// ---------- Payout requests ----------

export const requestPayout = createServerFn({ method: "POST" })
  .inputValidator(z.object({ amount: z.number().positive().max(100_000_000) }))
  .handler(async ({ data }) => {
    const { userId, supabaseAdmin } = await getAuthenticatedUser();
    // Profile required
    const { data: profile } = await supabaseAdmin
      .from("user_payout_profiles")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();
    if (!profile) {
      throw new Error("Configurá tu método de pago antes de solicitar un retiro.");
    }
    const { data: requestId, error } = await supabaseAdmin.rpc("request_payout", {
      _user_id: userId,
      _amount: data.amount,
    });
    if (error) throw new Error(error.message);
    return { requestId };
  });

export const getMyPayoutRequests = createServerFn({ method: "POST" })
  .handler(async () => {
    const { userId, supabaseAdmin } = await getAuthenticatedUser();
    const { data } = await supabaseAdmin
      .from("payout_requests")
      .select("*")
      .eq("user_id", userId)
      .order("requested_at", { ascending: false });
    return { requests: data || [] };
  });

// ---------- Resale listing fee preview ----------

export const previewResaleFees = createServerFn({ method: "POST" })
  .inputValidator(z.object({ salePrice: z.number().positive(), tenantId: z.string().uuid().optional().nullable() }))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: result, error } = await supabaseAdmin.rpc("compute_resale_fees", {
      _sale_price: data.salePrice,
      _tenant_id: data.tenantId || undefined,
    });
    if (error) throw new Error(error.message);
    const r = Array.isArray(result) ? result[0] : result;
    return { fees: r };
  });

// ---------- Admin payouts ----------

export const adminListPayouts = createServerFn({ method: "POST" })
  .inputValidator(z.object({ status: z.string().optional() }))
  .handler(async ({ data }) => {
    const { userId, supabaseAdmin } = await getAuthenticatedUser();
    await ensureAdmin(userId, supabaseAdmin);
    // Query payout_requests directly with service role to bypass auth.uid() checks in RPC.
    let q = supabaseAdmin
      .from("payout_requests")
      .select("*")
      .order("requested_at", { ascending: false })
      .limit(500);
    if (data.status) q = q.eq("status", data.status);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);

    // Enrich with email + display_name
    const userIds = Array.from(new Set((rows || []).map((r: any) => r.user_id)));
    let profilesMap: Record<string, { display_name: string | null }> = {};
    let emailsMap: Record<string, string> = {};
    if (userIds.length > 0) {
      const { data: profs } = await supabaseAdmin
        .from("profiles")
        .select("user_id, display_name")
        .in("user_id", userIds);
      (profs || []).forEach((p: any) => { profilesMap[p.user_id] = { display_name: p.display_name }; });
      // Fetch emails via auth admin
      for (const uid of userIds) {
        try {
          const { data: u } = await supabaseAdmin.auth.admin.getUserById(uid);
          if (u?.user?.email) emailsMap[uid] = u.user.email;
        } catch {}
      }
    }
    const enriched = (rows || []).map((r: any) => ({
      ...r,
      email: emailsMap[r.user_id] || "",
      display_name: profilesMap[r.user_id]?.display_name || null,
    }));
    console.log(`[adminListPayouts] status=${data.status || "all"} returned ${enriched.length} rows`);
    return { payouts: enriched };
  });

export const adminListPayoutsByUser = createServerFn({ method: "POST" })
  .handler(async () => {
    const { userId, supabaseAdmin } = await getAuthenticatedUser();
    await ensureAdmin(userId, supabaseAdmin);
    // Aggregate pending payouts per user, bypassing RPC's auth.uid() check.
    const { data: rows, error } = await supabaseAdmin
      .from("payout_requests")
      .select("user_id, amount, requested_at")
      .eq("status", "pending");
    if (error) throw new Error(error.message);

    const byUser = new Map<string, { user_id: string; total_pending_amount: number; request_count: number; oldest_request_at: string }>();
    (rows || []).forEach((r: any) => {
      const cur = byUser.get(r.user_id) || { user_id: r.user_id, total_pending_amount: 0, request_count: 0, oldest_request_at: r.requested_at };
      cur.total_pending_amount += Number(r.amount || 0);
      cur.request_count += 1;
      if (new Date(r.requested_at) < new Date(cur.oldest_request_at)) cur.oldest_request_at = r.requested_at;
      byUser.set(r.user_id, cur);
    });
    const userIds = Array.from(byUser.keys());

    let profilesMap: Record<string, { display_name: string | null }> = {};
    let emailsMap: Record<string, string> = {};
    let payoutMap: Record<string, any> = {};
    if (userIds.length > 0) {
      const { data: profs } = await supabaseAdmin
        .from("profiles").select("user_id, display_name").in("user_id", userIds);
      (profs || []).forEach((p: any) => { profilesMap[p.user_id] = { display_name: p.display_name }; });
      const { data: pps } = await supabaseAdmin
        .from("user_payout_profiles").select("*").in("user_id", userIds);
      (pps || []).forEach((p: any) => { payoutMap[p.user_id] = p; });
      for (const uid of userIds) {
        try {
          const { data: u } = await supabaseAdmin.auth.admin.getUserById(uid);
          if (u?.user?.email) emailsMap[uid] = u.user.email;
        } catch {}
      }
    }
    const result = Array.from(byUser.values())
      .map((r) => ({
        ...r,
        email: emailsMap[r.user_id] || "",
        display_name: profilesMap[r.user_id]?.display_name || null,
        payout_method: payoutMap[r.user_id]?.method || null,
        payout_account: payoutMap[r.user_id] || null,
      }))
      .sort((a, b) => new Date(a.oldest_request_at).getTime() - new Date(b.oldest_request_at).getTime());
    return { rows: result };
  });

export const adminGetPayoutDetail = createServerFn({ method: "POST" })
  .inputValidator(z.object({ requestId: z.string().uuid() }))
  .handler(async ({ data }) => {
    const { userId, supabaseAdmin } = await getAuthenticatedUser();
    await ensureAdmin(userId, supabaseAdmin);
    const { data: req } = await supabaseAdmin
      .from("payout_requests")
      .select("*")
      .eq("id", data.requestId)
      .single();
    if (!req) throw new Error("Solicitud no encontrada");
    const { data: profile } = await supabaseAdmin
      .from("user_payout_profiles")
      .select("*")
      .eq("user_id", req.user_id)
      .maybeSingle();
    const { data: walletRows } = await supabaseAdmin
      .from("user_wallets")
      .select("*")
      .eq("user_id", req.user_id)
      .maybeSingle();
    return { request: req, profile, wallet: walletRows };
  });

export const adminMarkPayoutPaid = createServerFn({ method: "POST" })
  .inputValidator(z.object({
    requestId: z.string().uuid(),
    reference: z.string().max(200).optional(),
    notes: z.string().max(1000).optional(),
  }))
  .handler(async ({ data }) => {
    const { userId, supabaseAdmin } = await getAuthenticatedUser();
    await ensureAdmin(userId, supabaseAdmin);
    const { error } = await supabaseAdmin.rpc("mark_payout_paid", {
      _admin_id: userId,
      _request_id: data.requestId,
      _reference: data.reference || undefined,
      _notes: data.notes || undefined,
    });
    if (error) throw new Error(error.message);
    return { success: true };
  });

export const adminRejectPayout = createServerFn({ method: "POST" })
  .inputValidator(z.object({ requestId: z.string().uuid(), reason: z.string().min(1).max(1000) }))
  .handler(async ({ data }) => {
    const { userId, supabaseAdmin } = await getAuthenticatedUser();
    await ensureAdmin(userId, supabaseAdmin);
    const { error } = await supabaseAdmin.rpc("reject_payout", {
      _admin_id: userId,
      _request_id: data.requestId,
      _reason: data.reason,
    });
    if (error) throw new Error(error.message);
    return { success: true };
  });
